/**
 * Source-specific functions for extracting suggestions.
 * Each function extracts suggestions from a specific data source.
 */

import { SearchSuggestion, SuggestionOptions } from './types';
import {
  matchesQuery,
  truncateProductTitle,
  isValidQuery,
} from './suggestionUtils';

/**
 * Extract suggestions from search history.
 * Uses query field as-is (no transformation).
 * Applies matching based on current query.
 *
 * @param historyItems Array of history items with query field
 * @param query Current search query
 * @param options Suggestion options
 * @returns Array of matching history suggestions
 */
export function extractHistorySuggestions(
  historyItems: Array<{ query: string }>,
  query: string,
  options: SuggestionOptions = {}
): SearchSuggestion[] {
  const { includeStartsWith = true } = options;

  if (!isValidQuery(query, options.minQueryLength)) {
    return [];
  }

  const lowerQuery = query.toLowerCase();
  const suggestions: SearchSuggestion[] = [];

  for (const item of historyItems) {
    const historyQuery = item.query;
    if (matchesQuery(historyQuery, lowerQuery, includeStartsWith)) {
      suggestions.push(historyQuery);
    }
  }

  return suggestions;
}

/**
 * Extract suggestions from categories.
 * Uses title field as-is (no transformation).
 * Applies matching based on current query.
 *
 * @param categories Array of category items with title field
 * @param query Current search query
 * @param options Suggestion options
 * @returns Array of matching category suggestions
 */
export function extractCategorySuggestions(
  categories: Array<{ title: string }>,
  query: string,
  options: SuggestionOptions = {}
): SearchSuggestion[] {
  const { includeStartsWith = true } = options;

  if (!isValidQuery(query, options.minQueryLength)) {
    return [];
  }

  const lowerQuery = query.toLowerCase();
  const suggestions: SearchSuggestion[] = [];

  for (const category of categories) {
    const categoryTitle = category.title;
    if (matchesQuery(categoryTitle, lowerQuery, includeStartsWith)) {
      suggestions.push(categoryTitle);
    }
  }

  return suggestions;
}

/**
 * Extract suggestions from products.
 * Uses title field with truncation (2-4 word phrases).
 * Applies matching based on current query.
 *
 * @param products Array of product items with title field
 * @param query Current search query
 * @param options Suggestion options
 * @returns Array of matching product suggestions
 */
export function extractProductSuggestions(
  products: Array<{ title: string }>,
  query: string,
  options: SuggestionOptions = {}
): SearchSuggestion[] {
  const { includeStartsWith = true } = options;

  if (!isValidQuery(query, options.minQueryLength)) {
    return [];
  }

  const lowerQuery = query.toLowerCase();
  const suggestions: SearchSuggestion[] = [];

  for (const product of products) {
    const productTitle = product.title;

    // First check if product title matches query
    if (matchesQuery(productTitle, lowerQuery, includeStartsWith)) {
      // Extract short phrase from matching title
      const shortPhrase = truncateProductTitle(productTitle);
      if (shortPhrase && shortPhrase.length > 0) {
        suggestions.push(shortPhrase);
      }
    }
  }

  return suggestions;
}